// src/pages/SubjectsPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Premium 24News Subjects Page — Full version
//  Features:
//   ① i18n via useTranslation (en / fr / ar / de)
//   ② Working like + save with JWT auth token (optimistic UI + rollback)
//   ③ Share button per card (native share → clipboard fallback)
//   ④ Grid / List view toggle
//   ⑤ Active filter chips (removable)
//   ⑥ 5 sort options (recommended · most liked · most saved · newest · most commented)
//   ⑦ "New" badge on subjects added within the last 7 days
//   ⑧ Back-to-top button (appears after 400 px scroll)
//   ⑨ "/" keyboard shortcut to focus search
//   ⑩ Infinite scroll (IntersectionObserver auto load-more)
//   ⑪ Hero aggregate stats (total topics · total likes · total saves)
//   ⑫ Comments count on card
//   ⑬ Full RTL support (Arabic)
//   ⑭ Dark mode, reduced motion, fully responsive (mobile → desktop)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../i18n";
import { Helmet } from "react-helmet-async";
const autoDetectBase = (() => {
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1")
    return `${protocol}//${hostname}:8000`;
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
})();
export const API_BASE = process.env.REACT_APP_API_BASE || autoDetectBase;

// ── Helpers ───────────────────────────────────────────────────────────────────
function short(text, n = 120) {
  if (!text) return "";
  return text.length > n ? text.slice(0, n).trim() + "…" : text;
}

function formatCount(n) {
  if (typeof n !== "number") return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000)      return `${(Math.round(n / 100) / 10).toFixed(1)}k`;
  return `${n}`;
}

function highlightMatch(text = "", q = "") {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="sj-highlight">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function isNew(dateStr) {
  if (!dateStr) return false;
  return (Date.now() - new Date(dateStr).getTime()) < 7 * 24 * 60 * 60 * 1000;
}

// FIX ① — getTranslatedName defined here, outside any component
function getTranslatedName(category, lang) {
  const list = category?.translations || [];

  const normalize = (l) =>
    (l || "").toLowerCase().replace("_", "-").split("-")[0];

  const currentLang = normalize(lang);

  const match = list.find(
    (t) => normalize(t.target_lang) === currentLang
  );

  return match?.name || category?.name || "Category";
}

// ── Auth token helper ─────────────────────────────────────────────────────────
function getToken() {
  try {
    return (
      localStorage.getItem("accessToken") ||
      localStorage.getItem("token")       ||
      localStorage.getItem("authToken")   ||
      null
    );
  } catch { return null; }
}

function authHeaders(extra = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...extra };
  if (token) {
    headers["Authorization"] = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }
  return headers;
}

function normaliseItem(it) {
  return {
    ...it,
    likes_count:    Number(it.likes_count    || 0),
    saves_count:    Number(it.saves_count    || 0),
    comments_count: Number(it.comments_count || 0),
    is_liked:  !!it.is_liked,
    is_saved:  !!it.is_saved,
  };
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600;700&display=swap');

/* ── Design tokens ── */
:root {
  --sj-red:        #e63311;
  --sj-red-dk:     #c4280d;
  --sj-ink:        #0d0d0d;
  --sj-ink-2:      #3d3d3d;
  --sj-ink-3:      #767676;
  --sj-bg:         #ffffff;
  --sj-bg-2:       #f8f8f7;
  --sj-bg-3:       #f0eeec;
  --sj-border:     rgba(0,0,0,0.08);
  --sj-shadow:     0 2px 16px rgba(0,0,0,0.07);
  --sj-shadow-h:   0 12px 36px rgba(0,0,0,0.13);
  --sj-radius:     14px;
  --sj-ease:       cubic-bezier(.4,0,.2,1);
  --sj-serif:      'Playfair Display', Georgia, serif;
  --sj-sans:       'DM Sans', -apple-system, sans-serif;
  --sj-sidebar-w:  248px;
}

@media (prefers-color-scheme: dark) {
  :root {
    --sj-ink:      #f0ede8;
    --sj-ink-2:    #c9c5be;
    --sj-ink-3:    #888;
    --sj-bg:       #111110;
    --sj-bg-2:     #191917;
    --sj-bg-3:     #222220;
    --sj-border:   rgba(255,255,255,0.07);
    --sj-shadow:   0 2px 16px rgba(0,0,0,0.4);
    --sj-shadow-h: 0 12px 36px rgba(0,0,0,0.55);
  }
}

/* ── Keyframes ── */
@keyframes sj-up    { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
@keyframes sj-in    { from { opacity:0; }                             to { opacity:1; } }
@keyframes sj-pop   { 0%{transform:scale(.85)} 60%{transform:scale(1.1)} 100%{transform:scale(1)} }
@keyframes sj-shim  { from { background-position:-600px 0; }         to { background-position:600px 0; } }
@keyframes sj-bar   { from { transform:scaleX(0); }                  to { transform:scaleX(1); } }
@keyframes sj-chip  { from { opacity:0; transform:scale(.85); }      to { opacity:1; transform:scale(1); } }
@keyframes sj-btt   { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }

@media (prefers-reduced-motion:reduce) {
  *, *::before, *::after {
    animation: none !important;
    transition: none !important;
  }
}

/* ── Base ── */
.sj-page {
  font-family: var(--sj-sans);
  background:  var(--sj-bg);
  color:       var(--sj-ink);
  min-height:  100vh;
}

/* ── RTL ── */
[dir="rtl"] .sj-hero__eyebrow-bar { transform-origin: right; }
[dir="rtl"] .sj-cat-btn::before   { left: auto; right: 0; }
[dir="rtl"] .sj-cat-btn:hover,
[dir="rtl"] .sj-cat-btn.active    { padding-left: 10px; padding-right: 14px; }
[dir="rtl"] .sj-btt               { right: auto; left: 24px; }

/* ══════════════════════════════════════════════════════
   HERO
══════════════════════════════════════════════════════ */
.sj-hero {
  position:   relative;
  background: var(--sj-ink);
  overflow:   hidden;
  padding:    clamp(36px, 8vw, 80px) clamp(16px, 5vw, 64px) clamp(28px, 6vw, 60px);
  animation:  sj-in .4s var(--sj-ease) both;
}

.sj-hero__bg {
  position:   absolute;
  inset:      0;
  z-index:    0;
  background:
    radial-gradient(ellipse at 80% 40%, rgba(230,51,17,.2) 0%, transparent 60%),
    radial-gradient(ellipse at 10% 80%, rgba(230,51,17,.08) 0%, transparent 55%);
}

.sj-hero__noise {
  position:         absolute;
  inset:            0;
  z-index:          0;
  pointer-events:   none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.03'/%3E%3C/svg%3E");
}

.sj-hero__inner {
  position: relative;
  z-index:  1;
  max-width: 1280px;
  margin:   0 auto;
}

.sj-hero__eyebrow {
  display:        inline-flex;
  align-items:    center;
  gap:            8px;
  font-size:      .7rem;
  font-weight:    700;
  letter-spacing: .13em;
  text-transform: uppercase;
  color:          var(--sj-red);
  margin-bottom:  16px;
  animation:      sj-up .4s .1s var(--sj-ease) both;
}

.sj-hero__eyebrow-bar {
  display:          inline-block;
  width:            24px;
  height:           2px;
  background:       var(--sj-red);
  border-radius:    2px;
  transform-origin: left;
  animation:        sj-bar .5s .15s var(--sj-ease) both;
}

.sj-hero__title {
  font-family:    var(--sj-serif);
  font-size:      clamp(2rem, 5.5vw, 4rem);
  font-weight:    900;
  line-height:    1.06;
  letter-spacing: -.02em;
  color:          #fff;
  margin:         0 0 12px;
  animation:      sj-up .45s .15s var(--sj-ease) both;
  text-shadow:
    0 2px 4px rgba(0,0,0,.2),
    0 4px 8px rgba(0,0,0,.1);

}

.sj-hero__sub {
  font-size:   clamp(.9rem, 1.8vw, 1.05rem);
  font-weight: 300;
  color:       rgba(231, 24, 24, 0.98);
  max-width:   520px;
  line-height: 1.7;
  margin:      0 0 24px;
  animation:   sj-up .45s .2s var(--sj-ease) both;
  text-shadow:
    0 2px 4px rgba(214, 214, 214, 0.2),
    0 4px 8px rgba(175, 168, 168, 0.2);

}

/* ⑪ Aggregate stats in hero */
.sj-hero__stats {
  display:     flex;
  align-items: center;
  gap:         24px;
  flex-wrap:   wrap;
  animation:   sj-up .45s .25s var(--sj-ease) both;
  color:       rgba(8, 8, 8, 0.95);
}

.sj-hero__stat {
  display:     flex;
  align-items: baseline;
  gap:         6px;
}

.sj-hero__stat-num {
  font-family: var(--sj-serif);
  font-size:   1.5rem;
  font-weight: 700;
  color:       #000000;
  line-height: 1;
}

.sj-hero__stat-lbl {
  font-size:      .7rem;
  font-weight:    500;
  letter-spacing: .04em;
  color:          rgba(0, 0, 0, 0.4);
}

.sj-hero__stat-div {
  width:      1px;
  height:     24px;
  background: rgba(255,255,255,.1);
}

/* ══════════════════════════════════════════════════════
   SEARCH + CONTROLS BAR
══════════════════════════════════════════════════════ */
.sj-search-wrap {
  max-width: 1280px;
  margin:    0 auto;
  padding:   0 clamp(16px, 5vw, 64px);
  transform: translateY(-1px);
  animation: sj-up .4s .3s var(--sj-ease) both;
}

.sj-search-card {
  background:    var(--sj-bg);
  border:        1px solid var(--sj-border);
  border-radius: var(--sj-radius);
  box-shadow:    var(--sj-shadow);
  padding:       16px 20px;
  display:       flex;
  align-items:   flex-start;
  gap:           12px;
  flex-wrap:     wrap;
}

.sj-search-field-wrap {
  position:  relative;
  flex:      1;
  min-width: 220px;
}

.sj-search-input {
  width:         100%;
  box-sizing:    border-box;
  padding:       12px 44px 12px 16px;
  font-size:     .9rem;
  font-family:   var(--sj-sans);
  color:         var(--sj-ink);
  background:    var(--sj-bg-2);
  border:        1.5px solid var(--sj-border);
  border-radius: 10px;
  outline:       none;
  transition:    border-color .18s var(--sj-ease), box-shadow .18s var(--sj-ease);
}

.sj-search-input:focus {
  border-color: var(--sj-red);
  box-shadow:   0 0 0 3px rgba(230,51,17,.1);
  background:   var(--sj-bg);
}

.sj-search-input::placeholder { color: var(--sj-ink-3); }

.sj-search-icon {
  position:       absolute;
  right:          14px;
  top:            14px;
  color:          var(--sj-ink-3);
  pointer-events: none;
  display:        flex;
}

.sj-search-hint {
  font-size:   .68rem;
  color:       var(--sj-ink-3);
  margin-top:  5px;
  display:     flex;
  align-items: center;
  gap:         5px;
}

.sj-kbd {
  display:         inline-flex;
  align-items:     center;
  justify-content: center;
  width:           18px;
  height:          18px;
  border-radius:   4px;
  border:          1px solid var(--sj-border);
  background:      var(--sj-bg-3);
  font-size:       .6rem;
  font-weight:     700;
  color:           var(--sj-ink-3);
}

/* Suggestions dropdown */
.sj-suggestions {
  position:       absolute;
  top:            calc(100% + 8px);
  left:           0;
  right:          0;
  background:     var(--sj-bg);
  border:         1px solid var(--sj-border);
  border-radius:  12px;
  box-shadow:     var(--sj-shadow-h);
  z-index:        1200;
  overflow:       hidden;
  max-height:     280px;
  overflow-y:     auto;
  animation:      sj-up .2s var(--sj-ease) both;
  scrollbar-width: thin;
}

.sj-suggestion-item {
  display:       block;
  width:         100%;
  padding:       12px 16px;
  background:    none;
  border:        none;
  text-align:    left;
  cursor:        pointer;
  transition:    background .13s;
  border-bottom: 1px solid var(--sj-border);
  font-family:   var(--sj-sans);
}

.sj-suggestion-item:last-child { border-bottom: none; }
.sj-suggestion-item:hover,
.sj-suggestion-item.active     { background: var(--sj-bg-2); }
.sj-suggestion-name { font-size: .85rem; font-weight: 600; color: var(--sj-ink); }
.sj-suggestion-desc { font-size: .75rem; color: var(--sj-ink-3); margin-top: 2px; }
.sj-highlight { background: rgba(230,51,17,.12); color: var(--sj-red); border-radius: 2px; padding: 0 1px; font-style: normal; }

/* Controls row */
.sj-controls {
  display:     flex;
  align-items: center;
  gap:         8px;
  flex-wrap:   wrap;
  flex-shrink: 0;
}

.sj-select {
  padding:        10px 32px 10px 14px;
  font-size:      .8rem;
  font-weight:    600;
  font-family:    var(--sj-sans);
  color:          var(--sj-ink);
  background:     var(--sj-bg-2);
  border:         1.5px solid var(--sj-border);
  border-radius:  10px;
  outline:        none;
  cursor:         pointer;
  transition:     border-color .18s;
  appearance:     none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:   no-repeat;
  background-position: right 10px center;
}

.sj-select:focus { border-color: var(--sj-red); }

/* ④ View toggle */
.sj-view-toggle {
  display:    flex;
  border:     1.5px solid var(--sj-border);
  border-radius: 10px;
  overflow:   hidden;
}

.sj-view-btn {
  padding:         9px 12px;
  background:      var(--sj-bg-2);
  border:          none;
  cursor:          pointer;
  color:           var(--sj-ink-3);
  transition:      all .16s var(--sj-ease);
  display:         flex;
  align-items:     center;
}

.sj-view-btn:first-child { border-right: 1px solid var(--sj-border); }
.sj-view-btn.active      { background: var(--sj-red); color: #fff; }
.sj-view-btn:not(.active):hover { background: var(--sj-bg-3); color: var(--sj-ink); }

.sj-reset-btn {
  display:        inline-flex;
  align-items:    center;
  gap:            6px;
  padding:        10px 16px;
  font-size:      .78rem;
  font-weight:    700;
  letter-spacing: .04em;
  color:          var(--sj-ink-3);
  background:     var(--sj-bg-2);
  border:         1.5px solid var(--sj-border);
  border-radius:  10px;
  cursor:         pointer;
  transition:     all .18s var(--sj-ease);
  font-family:    var(--sj-sans);
}

.sj-reset-btn:hover { color: var(--sj-red); border-color: rgba(230,51,17,.3); background: rgba(230,51,17,.04); }

/* ⑤ Active filter chips */
.sj-chips {
  max-width:   1280px;
  margin:      0 auto;
  padding:     12px clamp(16px, 5vw, 64px) 0;
  display:     flex;
  align-items: center;
  gap:         8px;
  flex-wrap:   wrap;
  animation:   sj-in .3s var(--sj-ease) both;
}

.sj-chip {
  display:        inline-flex;
  align-items:    center;
  gap:            6px;
  padding:        5px 10px 5px 12px;
  font-size:      .72rem;
  font-weight:    700;
  letter-spacing: .03em;
  background:     rgba(230,51,17,.09);
  color:          var(--sj-red);
  border:         1px solid rgba(230,51,17,.2);
  border-radius:  100px;
  animation:      sj-chip .25s var(--sj-ease) both;
}

.sj-chip__remove {
  display:         inline-flex;
  align-items:     center;
  justify-content: center;
  width:           16px;
  height:          16px;
  border-radius:   50%;
  border:          none;
  background:      rgba(230,51,17,.15);
  color:           var(--sj-red);
  cursor:          pointer;
  transition:      background .15s;
  padding:         0;
}

.sj-chip__remove:hover { background: rgba(230,51,17,.3); }

.sj-chips__clear {
  font-size:     .72rem;
  font-weight:   600;
  color:         var(--sj-ink-3);
  background:    none;
  border:        none;
  cursor:        pointer;
  padding:       4px 8px;
  border-radius: 100px;
  transition:    color .15s;
  font-family:   var(--sj-sans);
}

.sj-chips__clear:hover { color: var(--sj-red); }

/* ══════════════════════════════════════════════════════
   LAYOUT
══════════════════════════════════════════════════════ */
.sj-layout {
  max-width: 1280px;
  margin:    0 auto;
  padding:   clamp(20px, 4vw, 40px) clamp(16px, 5vw, 64px);
  display:   grid;
  grid-template-columns: var(--sj-sidebar-w) 1fr;
  gap:       clamp(20px, 3vw, 40px);
  align-items: start;
}

@media (max-width: 1024px) { .sj-layout { grid-template-columns: 200px 1fr; } }
@media (max-width: 767px)  { .sj-layout { grid-template-columns: 1fr; } }

/* ══════════════════════════════════════════════════════
   SIDEBAR / CATEGORIES
══════════════════════════════════════════════════════ */
.sj-sidebar {
  position:  sticky;
  top:       80px;
  animation: sj-up .4s .2s var(--sj-ease) both;
}

.sj-sidebar-card {
  background:    var(--sj-bg-2);
  border:        1px solid var(--sj-border);
  border-radius: var(--sj-radius);
  overflow:      hidden;
}

.sj-sidebar-head {
  padding:         16px 18px;
  border-bottom:   1px solid var(--sj-border);
  display:         flex;
  align-items:     center;
  justify-content: space-between;
}

.sj-sidebar-head-left { display: flex; align-items: center; gap: 9px; }

.sj-sidebar-bar {
  width:         3px;
  height:        16px;
  background:    var(--sj-red);
  border-radius: 2px;
}

.sj-sidebar-title {
  font-size:      .75rem;
  font-weight:    700;
  letter-spacing: .09em;
  text-transform: uppercase;
  color:          var(--sj-ink-3);
  margin:         0;
}

.sj-mobile-cat-toggle {
  display:         none;
  width:           32px;
  height:          32px;
  border-radius:   8px;
  border:          1.5px solid var(--sj-border);
  background:      var(--sj-bg);
  align-items:     center;
  justify-content: center;
  cursor:          pointer;
  color:           var(--sj-ink-3);
  transition:      all .18s;
}

.sj-mobile-cat-toggle:hover { color: var(--sj-ink); background: var(--sj-bg-3); }

@media (max-width: 767px) {
  .sj-mobile-cat-toggle { display: flex; }
  .sj-cat-list-wrap {
    max-height: 0;
    overflow:   hidden;
    transition: max-height .3s var(--sj-ease);
  }
  .sj-cat-list-wrap.open { max-height: 800px; }
}

.sj-cat-list {
  padding:        10px 10px 12px;
  display:        flex;
  flex-direction: column;
  gap:            2px;
}

.sj-cat-btn {
  display:         flex;
  align-items:     center;
  justify-content: space-between;
  padding:         9px 10px;
  background:      none;
  border:          none;
  border-radius:   8px;
  cursor:          pointer;
  text-align:      left;
  transition:      all .16s var(--sj-ease);
  font-family:     var(--sj-sans);
  color:           var(--sj-ink-2);
  font-size:       .82rem;
  font-weight:     500;
  position:        relative;
}

.sj-cat-btn::before {
  content:          '';
  position:         absolute;
  left:             0;
  top:              20%;
  bottom:           20%;
  width:            2.5px;
  border-radius:    2px;
  background:       var(--sj-red);
  transform:        scaleY(0);
  transform-origin: center;
  transition:       transform .18s var(--sj-ease);
}

.sj-cat-btn:hover { background: var(--sj-bg-3); color: var(--sj-ink); padding-left: 14px; }
.sj-cat-btn:hover::before { transform: scaleY(1); }

.sj-cat-btn.active {
  background:   rgba(230,51,17,.07);
  color:        var(--sj-red);
  font-weight:  700;
  padding-left: 14px;
}

.sj-cat-btn.active::before { transform: scaleY(1); }

.sj-cat-dot {
  width:         6px;
  height:        6px;
  border-radius: 50%;
  background:    var(--sj-red);
  flex-shrink:   0;
}

.sj-cat-footer {
  padding:     12px 18px;
  border-top:  1px solid var(--sj-border);
  font-size:   .72rem;
  color:       var(--sj-ink-3);
  font-weight: 500;
}

/* ══════════════════════════════════════════════════════
   MAIN AREA
══════════════════════════════════════════════════════ */
.sj-main { min-width: 0; }

.sj-results-head {
  display:     flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap:   wrap;
  gap:         8px;
}

.sj-results-count { font-size: .78rem; font-weight: 600; color: var(--sj-ink-3); }
.sj-results-count strong { color: var(--sj-ink); font-weight: 700; }

/* ══════════════════════════════════════════════════════
   GRID VIEW
══════════════════════════════════════════════════════ */
.sj-grid {
  display:               grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap:                   18px;
}

@media (max-width: 480px) { .sj-grid { grid-template-columns: 1fr; gap: 14px; } }

/* ══════════════════════════════════════════════════════
   LIST VIEW
══════════════════════════════════════════════════════ */
.sj-list {
  display:        flex;
  flex-direction: column;
  gap:            12px;
}

/* ══════════════════════════════════════════════════════
   CARD (grid mode)
══════════════════════════════════════════════════════ */
.sj-card {
  background:    var(--sj-bg);
  border:        1px solid var(--sj-border);
  border-radius: var(--sj-radius);
  overflow:      hidden;
  display:       flex;
  flex-direction: column;
  transition:    transform .22s var(--sj-ease), box-shadow .22s var(--sj-ease), border-color .22s var(--sj-ease);
  animation:     sj-up .4s var(--sj-ease) both;
}

.sj-card:hover {
  transform:    translateY(-5px);
  box-shadow:   var(--sj-shadow-h);
  border-color: rgba(230,51,17,.15);
}

.sj-card__strip {
  height:          3px;
  background:      var(--sj-red);
  transform-origin: left;
  transform:       scaleX(0);
  transition:      transform .25s var(--sj-ease);
}

.sj-card:hover .sj-card__strip { transform: scaleX(1); }

.sj-card__media {
  position:    relative;
  overflow:    hidden;
  background:  var(--sj-bg-3);
  aspect-ratio: 16/7;
  flex-shrink: 0;
}

.sj-card__cover {
  width:      100%;
  height:     100%;
  object-fit: cover;
  display:    block;
  transition: transform .4s var(--sj-ease);
}

.sj-card:hover .sj-card__cover { transform: scale(1.05); }

.sj-card__icon-box {
  width:           100%;
  height:          100%;
  display:         flex;
  align-items:     center;
  justify-content: center;
  font-size:       2.5rem;
  background:      linear-gradient(135deg, var(--sj-bg-2), var(--sj-bg-3));
}

.sj-card__cat-tag {
  position:       absolute;
  top:            10px;
  left:           10px;
  font-size:      .62rem;
  font-weight:    700;
  letter-spacing: .09em;
  text-transform: uppercase;
  background:     var(--sj-red);
  color:          #fff;
  padding:        3px 8px;
  border-radius:  4px;
  line-height:    1;
}

/* ⑦ New badge */
.sj-card__new-badge {
  position:       absolute;
  top:            10px;
  right:          10px;
  font-size:      .6rem;
  font-weight:    800;
  letter-spacing: .08em;
  text-transform: uppercase;
  background:     #22c55e;
  color:          #fff;
  padding:        3px 8px;
  border-radius:  4px;
  line-height:    1;
}

.sj-card__body {
  padding: clamp(14px, 3vw, 18px);
  flex:    1;
  display: flex;
  flex-direction: column;
  gap:     10px;
}

.sj-card__name {
  font-family:      var(--sj-serif);
  font-size:        clamp(1rem, 2vw, 1.1rem);
  font-weight:      700;
  line-height:      1.25;
  color:            var(--sj-ink);
  margin:           0;
  transition:       color .18s;
  text-decoration:  none;
  display:          -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow:         hidden;
}

.sj-card:hover .sj-card__name { color: var(--sj-red); }

.sj-card__desc {
  font-size:         .8rem;
  color:             var(--sj-ink-3);
  line-height:       1.55;
  display:           -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow:          hidden;
  margin:            0;
}

.sj-card__stats {
  margin-top:     auto;
  padding-top:    12px;
  border-top:     1px solid var(--sj-border);
  display:        flex;
  align-items:    center;
  justify-content: space-between;
  gap:            8px;
  flex-wrap:      wrap;
}

.sj-card__meta-left {
  display:     flex;
  align-items: center;
  gap:         10px;
  flex-wrap:   wrap;
}

.sj-card__stat-item {
  display:     flex;
  align-items: center;
  gap:         4px;
  font-size:   .7rem;
  font-weight: 600;
  color:       var(--sj-ink-3);
}

.sj-card__actions { display: flex; align-items: center; gap: 6px; }

/* Action buttons (like / save / share) */
.sj-action-btn {
  display:        inline-flex;
  align-items:    center;
  gap:            5px;
  padding:        6px 10px;
  font-size:      .7rem;
  font-weight:    700;
  border-radius:  100px;
  border:         1.5px solid var(--sj-border);
  background:     var(--sj-bg-2);
  cursor:         pointer;
  color:          var(--sj-ink-3);
  transition:     all .18s var(--sj-ease);
  font-family:    var(--sj-sans);
  line-height:    1;
}

.sj-action-btn:hover { transform: scale(1.04); }

.sj-action-btn--like:hover    { border-color: #f87171; color: #ef4444; background: rgba(239,68,68,.06); }
.sj-action-btn--like.active   { border-color: #f87171; color: #ef4444; background: rgba(239,68,68,.08); }
.sj-action-btn--like.active svg { fill: currentColor; }

.sj-action-btn--save:hover    { border-color: #60a5fa; color: #3b82f6; background: rgba(59,130,246,.06); }
.sj-action-btn--save.active   { border-color: #60a5fa; color: #3b82f6; background: rgba(59,130,246,.08); }
.sj-action-btn--save.active svg { fill: currentColor; }

.sj-action-btn--share:hover   { border-color: rgba(230,51,17,.3); color: var(--sj-red); background: rgba(230,51,17,.05); }
.sj-action-btn--share.copied  { border-color: #22c55e; color: #22c55e; background: rgba(34,197,94,.06); }

.sj-action-btn.anim { animation: sj-pop .42s var(--sj-ease) both; }
.sj-action-count    { font-size: .68rem; font-weight: 700; }

/* ══════════════════════════════════════════════════════
   LIST CARD (list mode)
══════════════════════════════════════════════════════ */
.sj-list-card {
  background:    var(--sj-bg);
  border:        1px solid var(--sj-border);
  border-radius: var(--sj-radius);
  overflow:      hidden;
  display:       flex;
  align-items:   center;
  transition:    transform .2s var(--sj-ease), box-shadow .2s var(--sj-ease), border-color .2s var(--sj-ease);
  animation:     sj-up .35s var(--sj-ease) both;
}

.sj-list-card:hover {
  transform:    translateX(4px);
  box-shadow:   var(--sj-shadow-h);
  border-color: rgba(230,51,17,.15);
}

.sj-list-card__img-wrap {
  width:      80px;
  height:     80px;
  flex-shrink: 0;
  overflow:   hidden;
  background: var(--sj-bg-3);
}

.sj-list-card__img {
  width:      100%;
  height:     100%;
  object-fit: cover;
  transition: transform .35s var(--sj-ease);
}

.sj-list-card:hover .sj-list-card__img { transform: scale(1.08); }

.sj-list-card__icon {
  width:           100%;
  height:          100%;
  display:         flex;
  align-items:     center;
  justify-content: center;
  font-size:       1.75rem;
  background:      linear-gradient(135deg, var(--sj-bg-2), var(--sj-bg-3));
}

.sj-list-card__body {
  flex:        1;
  min-width:   0;
  padding:     14px 16px;
  display:     flex;
  align-items: center;
  gap:         12px;
  flex-wrap:   wrap;
}

.sj-list-card__info { flex: 1; min-width: 0; }

.sj-list-card__badges {
  display:     flex;
  align-items: center;
  gap:         6px;
  margin-bottom: 4px;
}

.sj-list-card__new {
  font-size:      .6rem;
  font-weight:    800;
  letter-spacing: .07em;
  text-transform: uppercase;
  background:     #22c55e;
  color:          #fff;
  padding:        2px 7px;
  border-radius:  4px;
}

.sj-list-card__cat {
  font-size:      .62rem;
  font-weight:    700;
  letter-spacing: .07em;
  text-transform: uppercase;
  background:     rgba(230,51,17,.1);
  color:          var(--sj-red);
  padding:        2px 8px;
  border-radius:  4px;
}

.sj-list-card__name {
  font-family:     var(--sj-serif);
  font-size:       .95rem;
  font-weight:     700;
  color:           var(--sj-ink);
  text-decoration: none;
  transition:      color .18s;
  white-space:     nowrap;
  overflow:        hidden;
  text-overflow:   ellipsis;
  display:         block;
}

.sj-list-card:hover .sj-list-card__name { color: var(--sj-red); }

.sj-list-card__desc {
  font-size:     .75rem;
  color:         var(--sj-ink-3);
  margin-top:    3px;
  white-space:   nowrap;
  overflow:      hidden;
  text-overflow: ellipsis;
}

.sj-list-card__meta {
  display:     flex;
  align-items: center;
  gap:         10px;
  flex-shrink: 0;
  flex-wrap:   wrap;
}

.sj-list-card__stat {
  display:     flex;
  align-items: center;
  gap:         4px;
  font-size:   .7rem;
  font-weight: 600;
  color:       var(--sj-ink-3);
}

/* ══════════════════════════════════════════════════════
   EMPTY / ERROR / LOAD MORE
══════════════════════════════════════════════════════ */
.sj-empty {
  grid-column:   1 / -1;
  padding:       60px 24px;
  text-align:    center;
  border:        1.5px dashed var(--sj-border);
  border-radius: var(--sj-radius);
  background:    var(--sj-bg-2);
}

.sj-empty__icon  { font-size: 3rem; margin-bottom: 16px; opacity: .4; }
.sj-empty__title { font-family: var(--sj-serif); font-size: 1.3rem; font-weight: 700; margin: 0 0 8px; }
.sj-empty__sub   { font-size: .875rem; color: var(--sj-ink-3); margin: 0; }

.sj-error {
  padding:       20px;
  background:    rgba(239,68,68,.07);
  border:        1px solid rgba(239,68,68,.2);
  border-radius: 10px;
  font-size:     .875rem;
  color:         #ef4444;
  margin-bottom: 20px;
}

.sj-load-more {
  margin-top:      36px;
  display:         flex;
  align-items:     center;
  justify-content: center;
  gap:             12px;
}

.sj-load-btn {
  display:        inline-flex;
  align-items:    center;
  gap:            8px;
  padding:        13px 32px;
  font-size:      .8rem;
  font-weight:    700;
  letter-spacing: .06em;
  text-transform: uppercase;
  background:     var(--sj-ink);
  color:          var(--sj-bg);
  border:         none;
  border-radius:  100px;
  cursor:         pointer;
  transition:     background .18s var(--sj-ease), transform .18s var(--sj-ease);
  font-family:    var(--sj-sans);
}

.sj-load-btn:hover    { background: var(--sj-red); transform: translateY(-2px); }
.sj-load-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }

.sj-end-marker {
  font-size:      .75rem;
  font-weight:    600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color:          var(--sj-ink-3);
  display:        flex;
  align-items:    center;
  gap:            12px;
}

.sj-end-marker::before,
.sj-end-marker::after {
  content:    '';
  flex:       1;
  height:     1px;
  background: var(--sj-border);
  display:    inline-block;
  max-width:  80px;
}

/* ⑩ Infinite scroll sentinel */
.sj-sentinel { height: 1px; width: 100%; }

/* ⑧ Back to top button */
.sj-btt {
  position:        fixed;
  bottom:          28px;
  right:           24px;
  z-index:         900;
  width:           44px;
  height:          44px;
  border-radius:   50%;
  background:      var(--sj-ink);
  color:           #fff;
  border:          none;
  cursor:          pointer;
  display:         flex;
  align-items:     center;
  justify-content: center;
  box-shadow:      0 4px 20px rgba(0,0,0,.22);
  transition:      background .18s var(--sj-ease), transform .18s var(--sj-ease);
  animation:       sj-btt .3s var(--sj-ease) both;
}

.sj-btt:hover { background: var(--sj-red); transform: translateY(-3px); }

/* ══════════════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════════════ */
.sj-skel {
  background:      linear-gradient(90deg, var(--sj-bg-2) 25%, var(--sj-bg-3) 50%, var(--sj-bg-2) 75%);
  background-size: 600px 100%;
  animation:       sj-shim 1.4s infinite linear;
  border-radius:   6px;
}

.sj-skel-card {
  border:        1px solid var(--sj-border);
  border-radius: var(--sj-radius);
  overflow:      hidden;
  background:    var(--sj-bg);
}

.sj-skel-card__img  { height: 100px; }
.sj-skel-card__body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.sj-skel-line       { height: 12px; }
.sj-skel-line--30   { width: 30%; }
.sj-skel-line--60   { width: 60%; }
.sj-skel-line--80   { width: 80%; }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icons = {
  Search:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>,
  Heart:    ({ filled }) => <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20.8 6.6c-1.9-3-6-3.4-8.1-.9-2.1-2.5-6.2-2.1-8.1.9-1.9 3 .1 7.8 5.5 11.6L12 21l2.6-2.9c5.4-3.8 7.4-8.6 5.6-11.5z"/></svg>,
  Bookmark: ({ filled }) => <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  Share:    () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  Check:    () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Article:  () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  Comment:  () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  Grid:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  List:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  ChevDown: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>,
  ChevUp:   () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>,
  ArrowUp:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  Down:     () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  X:        () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonGrid() {
  return (
    <div className="sj-grid">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="sj-skel-card" style={{ animationDelay: `${i * .06}s` }}>
          <div className="sj-skel sj-skel-card__img" />
          <div className="sj-skel-card__body">
            <div className="sj-skel sj-skel-line sj-skel-line--30" />
            <div className="sj-skel sj-skel-line sj-skel-line--80" />
            <div className="sj-skel sj-skel-line sj-skel-line--60" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Grid Card ─────────────────────────────────────────────────────────────────
function GridCard({ subject, index, onLike, onSave, onShare, sharedId, t }) {
  const primaryCat = Array.isArray(subject.subject_categories) && subject.subject_categories.length
    ? subject.subject_categories[0] : null;

  return (
    <div className="sj-card" style={{ animationDelay: `${0.04 + index * 0.05}s` }}>
      <div className="sj-card__strip" aria-hidden />

      <div className="sj-card__media">
        {subject.image_url
          ? <img src={subject.image_url} alt={subject.name} className="sj-card__cover" loading="lazy" />
          : (
            <div className="sj-card__icon-box">
              {subject.icon
                ? <span style={{ fontSize: "2.5rem" }} dangerouslySetInnerHTML={{ __html: subject.icon }} />
                : <span style={{ fontSize: "2rem" }}>📚</span>
              }
            </div>
          )
        }
        {primaryCat && <span className="sj-card__cat-tag">{primaryCat.name}</span>}
        {isNew(subject.created_at) && <span className="sj-card__new-badge">{t("new_badge")}</span>}
      </div>

      <div className="sj-card__body">
        <Link to={`/subject/${subject.slug}`} className="sj-card__name">{subject.name}</Link>
        {subject.description && <p className="sj-card__desc">{short(subject.description, 90)}</p>}

        <div className="sj-card__stats">
          <div className="sj-card__meta-left">
            {subject.comments_count > 0 && (
              <div className="sj-card__stat-item">
                <Icons.Comment />
                {formatCount(subject.comments_count)}
              </div>
            )}
          </div>

          <div className="sj-card__actions">
            <button
              className={`sj-action-btn sj-action-btn--like${subject.is_liked ? " active" : ""}${subject._likeAnim ? " anim" : ""}`}
              onClick={() => onLike(subject.id)}
              aria-pressed={!!subject.is_liked}
              aria-label={subject.is_liked ? t("unlike") : t("like")}
            >
              <Icons.Heart filled={!!subject.is_liked} />
              <span className="sj-action-count">{formatCount(subject.likes_count)}</span>
            </button>

            <button
              className={`sj-action-btn sj-action-btn--save${subject.is_saved ? " active" : ""}${subject._saveAnim ? " anim" : ""}`}
              onClick={() => onSave(subject.id)}
              aria-pressed={!!subject.is_saved}
              aria-label={subject.is_saved ? t("unsave") : t("save")}
            >
              <Icons.Bookmark filled={!!subject.is_saved} />
              <span className="sj-action-count">{formatCount(subject.saves_count)}</span>
            </button>

            <button
              className={`sj-action-btn sj-action-btn--share${sharedId === subject.id ? " copied" : ""}`}
              onClick={() => onShare(subject)}
              aria-label={t("share")}
            >
              {sharedId === subject.id ? <Icons.Check /> : <Icons.Share />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── List Card ─────────────────────────────────────────────────────────────────
function ListCard({ subject, index, onLike, onSave, onShare, sharedId, t }) {
  const primaryCat = Array.isArray(subject.subject_categories) && subject.subject_categories.length
    ? subject.subject_categories[0] : null;

  return (
    <div className="sj-list-card" style={{ animationDelay: `${0.03 + index * 0.04}s` }}>
      <div className="sj-list-card__img-wrap">
        {subject.image_url
          ? <img src={subject.image_url} alt={subject.name} className="sj-list-card__img" loading="lazy" />
          : (
            <div className="sj-list-card__icon">
              {subject.icon
                ? <span dangerouslySetInnerHTML={{ __html: subject.icon }} />
                : "📚"
              }
            </div>
          )
        }
      </div>

      <div className="sj-list-card__body">
        <div className="sj-list-card__info">
          <div className="sj-list-card__badges">
            {isNew(subject.created_at) && <span className="sj-list-card__new">{t("new_badge")}</span>}
            {primaryCat && <span className="sj-list-card__cat">{primaryCat.name}</span>}
          </div>
          <Link to={`/subject/${subject.slug}`} className="sj-list-card__name">{subject.name}</Link>
          {subject.description && <div className="sj-list-card__desc">{short(subject.description, 80)}</div>}
        </div>

        <div className="sj-list-card__meta">
          <div className="sj-list-card__stat">
            <Icons.Article /> {formatCount(subject.articles_count || 0)}
          </div>
          {subject.comments_count > 0 && (
            <div className="sj-list-card__stat">
              <Icons.Comment /> {formatCount(subject.comments_count)}
            </div>
          )}

          <button
            className={`sj-action-btn sj-action-btn--like${subject.is_liked ? " active" : ""}${subject._likeAnim ? " anim" : ""}`}
            onClick={() => onLike(subject.id)}
            aria-pressed={!!subject.is_liked}
            aria-label={subject.is_liked ? t("unlike") : t("like")}
          >
            <Icons.Heart filled={!!subject.is_liked} />
            <span className="sj-action-count">{formatCount(subject.likes_count)}</span>
          </button>

          <button
            className={`sj-action-btn sj-action-btn--save${subject.is_saved ? " active" : ""}${subject._saveAnim ? " anim" : ""}`}
            onClick={() => onSave(subject.id)}
            aria-pressed={!!subject.is_saved}
            aria-label={subject.is_saved ? t("unsave") : t("save")}
          >
            <Icons.Bookmark filled={!!subject.is_saved} />
            <span className="sj-action-count">{formatCount(subject.saves_count)}</span>
          </button>

          <button
            className={`sj-action-btn sj-action-btn--share${sharedId === subject.id ? " copied" : ""}`}
            onClick={() => onShare(subject)}
            aria-label={t("share")}
          >
            {sharedId === subject.id ? <Icons.Check /> : <Icons.Share />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SubjectsPage() {
  const { t, lang } = useTranslation();
  // FIX ② — nLang derived here, inside the component, after useTranslation
  const nLang = lang ? String(lang).split("-")[0].toLowerCase() : "en";



  // ── State ─────────────────────────────────────────────────────────────────
  const [categories,    setCategories]    = useState([]);
  const [selectedCat,   setSelectedCat]   = useState("all");
  const [catOpen,       setCatOpen]       = useState(false);
  const [items,         setItems]         = useState([]);
  const [nextUrl,       setNextUrl]       = useState(null);
  const [metaCount,     setMetaCount]     = useState(null);
  const [loadingCats,   setLoadingCats]   = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [err,           setErr]           = useState("");
  const [query,         setQuery]         = useState("");
  const [searchTerm,    setSearchTerm]    = useState("");
  const [suggestions,   setSuggestions]   = useState([]);
  const [showSuggest,   setShowSuggest]   = useState(false);
  const [suggestLoad,   setSuggestLoad]   = useState(false);
  const [highlighted,   setHighlighted]   = useState(-1);
  const [sortBy,        setSortBy]        = useState("recommended");
  const [viewMode,      setViewMode]      = useState("grid");
  const [showBtt,       setShowBtt]       = useState(false);
  const [sharedId,      setSharedId]      = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // ── apiFetch ──────────────────────────────────────────────────────────────
  const apiFetch = useCallback(async (url, opts = {}) => {
    const token   = getToken();
    const headers = new Headers(opts.headers || {});
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (token) {
      headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`);
      opts.credentials = opts.credentials || "omit";
    } else {
      opts.credentials = opts.credentials || "include";
    }
    const res = await fetch(url, { ...opts, headers });
    if (res.status === 401) {
      setShowAuthModal(true);
      const err = new Error("Unauthorized");
      err.response = res;
      throw err;
    }
    return res;
  }, []);

  // Aggregate stats for hero
  const totalLikes = useMemo(() => items.reduce((acc, s) => acc + (s.likes_count || 0), 0), [items]);
  const totalSaves = useMemo(() => items.reduce((acc, s) => acc + (s.saves_count || 0), 0), [items]);

  const abortRef     = useRef(null);
  const suggestAbort = useRef(null);
  const debounceRef  = useRef(null);
  const searchRef    = useRef(null);
  const suggestRef   = useRef(null);
  const sentinelRef  = useRef(null);

  // ⑧ Back to top scroll listener
  useEffect(() => {
    const onScroll = () => setShowBtt(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ⑨ "/" keyboard shortcut to focus search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Load categories ───────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingCats(true);
    fetch(`${API_BASE}/subject-categories/?tree=4&page_size=200`, {
      headers: authHeaders({ "Accept-Language": lang }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        const list = Array.isArray(d) ? d : d.results || [];
        const flat = [];
        function walk(nodes) {
          nodes.forEach(n => {
            flat.push({ id: n.id, slug: n.slug, name: n.name, translations: n.translations || [] });
            if (Array.isArray(n.children) && n.children.length) walk(n.children);
          });
        }
        walk(list);
        setCategories(flat);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));
  }, [lang]);

  // ── Load subjects ─────────────────────────────────────────────────────────
  const loadSubjects = useCallback(async (signal) => {
    setLoading(true);
    setErr("");
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (selectedCat && selectedCat !== "all") params.set("subject_category", selectedCat);
    if (sortBy === "newest")         params.set("ordering", "-created_at");
    if (sortBy === "most_commented") params.set("ordering", "-comments_count");
    params.set("page_size", "36");
    params.set("lang", lang);

    try {
      const res = await fetch(`${API_BASE}/subjects/?${params}`, {
        signal,
        headers: authHeaders({ "Accept-Language": lang }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => []);
      let list = Array.isArray(data) ? data : data.results || [];
      list = list.map(normaliseItem);
      if (sortBy === "most_liked")     list.sort((a, b) => b.likes_count - a.likes_count);
      if (sortBy === "most_saved")     list.sort((a, b) => b.saves_count - a.saves_count);
      if (sortBy === "most_commented") list.sort((a, b) => b.comments_count - a.comments_count);
      setItems(list);
      setNextUrl(!Array.isArray(data) ? data.next || null : null);
      setMetaCount(!Array.isArray(data) ? data.count ?? null : null);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setErr(t("load_error"));
      setItems([]);
      setNextUrl(null);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCat, sortBy, lang, t]);

  useEffect(() => {
    abortRef.current?.abort?.();
    const abort = new AbortController();
    abortRef.current = abort;
    loadSubjects(abort.signal);
    return () => abort.abort();
  }, [loadSubjects]);

  // ── Load more ─────────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (!nextUrl || loadingMore) return;
    abortRef.current?.abort?.();
    const abort = new AbortController();
    abortRef.current = abort;
    setLoadingMore(true);
    fetch(nextUrl, { signal: abort.signal, headers: authHeaders({ "Accept-Language": lang }) })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results || [];
        setItems(p => [...p, ...list.map(normaliseItem)]);
        setNextUrl(!Array.isArray(data) ? data.next || null : null);
      })
      .catch(() => setErr(t("load_more_error")))
      .finally(() => setLoadingMore(false));
  }, [nextUrl, loadingMore, lang, t]);

  // ⑩ Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextUrl || loadingMore) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [nextUrl, loadMore, loadingMore]);

  // ── Debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchTerm(query.trim()), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, selectedCat]);

  // ── Suggestions ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) { setSuggestions([]); setShowSuggest(false); setHighlighted(-1); return; }
    suggestAbort.current?.abort?.();
    const abort = new AbortController();
    suggestAbort.current = abort;
    setSuggestLoad(true);
    const params = new URLSearchParams({ search: query.trim(), page_size: "6" });
    if (selectedCat && selectedCat !== "all") params.set("subject_category", selectedCat);
    fetch(`${API_BASE}/subjects/?${params}`, { signal: abort.signal, headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        const l = Array.isArray(d) ? d : d.results || [];
        setSuggestions(l.slice(0, 6));
        setShowSuggest(true);
        setHighlighted(-1);
      })
      .catch(e => { if (e?.name !== "AbortError") setSuggestions([]); })
      .finally(() => setSuggestLoad(false));
    return () => abort.abort();
  }, [query, selectedCat]);

  function onSelectSuggestion(s) {
    setQuery(s.name || "");
    setSearchTerm(s.name || "");
    setShowSuggest(false);
    searchRef.current?.blur();
  }

  function onKeyDown(e) {
    if (!showSuggest || !suggestions.length) return;
    if (e.key === "ArrowDown")                      { e.preventDefault(); setHighlighted(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp")                   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlighted >= 0) { e.preventDefault(); onSelectSuggestion(suggestions[highlighted]); }
    else if (e.key === "Escape")                    { setShowSuggest(false); }
  }

  // ② Like — with auth token + optimistic UI
  const toggleLike = useCallback(async (id) => {
    const prev = items.find(s => s.id === id);
    if (!prev) return;

    setItems(p => p.map(s => s.id === id
      ? { ...s, is_liked: !s.is_liked, likes_count: Math.max(0, s.likes_count + (s.is_liked ? -1 : 1)), _likeAnim: true }
      : s
    ));

    try {
      const res = await apiFetch(`${API_BASE}/subjects/${prev.slug}/like/`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(res.status);
      const d = await res.json();
      setItems(p => p.map(s => s.id === id
        ? { ...s, likes_count: Number(d.likes_count ?? s.likes_count), is_liked: !!d.liked, _likeAnim: true }
        : s
      ));
      setTimeout(() => setItems(p => p.map(s => s.id === id ? { ...s, _likeAnim: false } : s)), 520);
    } catch {
      setItems(p => p.map(s => s.id === id ? prev : s));
    }
  }, [items, apiFetch]);

  // ② Save — with auth token + optimistic UI
  const toggleSave = useCallback(async (id) => {
    const prev = items.find(s => s.id === id);
    if (!prev) return;

    setItems(p => p.map(s => s.id === id
      ? { ...s, is_saved: !s.is_saved, saves_count: Math.max(0, s.saves_count + (s.is_saved ? -1 : 1)), _saveAnim: true }
      : s
    ));

    try {
      const res = await apiFetch(`${API_BASE}/subjects/${prev.slug}/save/`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(res.status);
      const d = await res.json();
      setItems(p => p.map(s => s.id === id
        ? { ...s, saves_count: Number(d.saves_count ?? s.saves_count), is_saved: !!d.saved, _saveAnim: true }
        : s
      ));
      setTimeout(() => setItems(p => p.map(s => s.id === id ? { ...s, _saveAnim: false } : s)), 520);
    } catch {
      setItems(p => p.map(s => s.id === id ? prev : s));
    }
  }, [items, apiFetch]);

  // ③ Share per card
  const shareSubject = useCallback((subject) => {
    const url = `${window.location.origin}/subject/${subject.slug}`;
    if (navigator.share) {
      navigator.share({ title: subject.name, url }).catch(() => {});
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      setSharedId(subject.id);
      setTimeout(() => setSharedId(null), 2200);
    });
  }, []);

  const categoriesMenu = useMemo(() => [
    { id: "all", slug: "all", name: t("all_topics"), translations: [] },
    ...categories,
  ], [categories, t]);

  const displayCount = metaCount ?? items.length;

  // ⑤ Active filter chips
  const activeChips = [];
  if (searchTerm) activeChips.push({ key: "search", label: `"${searchTerm}"`, onRemove: () => { setQuery(""); setSearchTerm(""); } });
  if (selectedCat !== "all") {
    const foundCat = categoriesMenu.find(c => String(c.slug) === String(selectedCat));
const catName = foundCat ? getTranslatedName(foundCat, nLang) : selectedCat;
    activeChips.push({ key: "cat", label: catName, onRemove: () => setSelectedCat("all") });
  }
  if (sortBy !== "recommended") {
    const sortLabels = {
      most_liked:     t("sort_most_liked"),
      most_saved:     t("sort_most_saved"),
      newest:         t("sort_newest"),
      most_commented: t("sort_most_commented"),
    };
    activeChips.push({ key: "sort", label: sortLabels[sortBy] || sortBy, onRemove: () => setSortBy("recommended") });
  }

  function resetAll() {
    setSelectedCat("all");
    setQuery("");
    setSearchTerm("");
    setSortBy("recommended");
  }


  return (
    <div className="sj-page">
      <style>{CSS}</style>

      {/* ══════════════════════════ HERO ══════════════════════════════════ */}
      <section className="sj-hero">
        <Helmet>
  <title>{t("explore_topics") || "Explore Topics"} | 24News</title>
  <meta
    name="description"
    content={t("subjects_sub") || "Browse topics, categories, and subjects on 24News."}
  />
  <meta property="og:type" content="website" />
  <meta property="og:title" content={t("explore_topics") || "Explore Topics"} />
  <meta
    property="og:description"
    content={t("subjects_sub") || "Browse topics, categories, and subjects on 24News."}
  />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={t("explore_topics") || "Explore Topics"} />
  <meta
    name="twitter:description"
    content={t("subjects_sub") || "Browse topics, categories, and subjects on 24News."}
  />
  <link rel="canonical" href={`${window.location.origin}/${lang}/subjects`} />
</Helmet>
        <div className="sj-hero__bg"    aria-hidden />
        <div className="sj-hero__noise" aria-hidden />
        <div className="sj-hero__inner">
          <div className="sj-hero__eyebrow">
            <span className="sj-hero__eyebrow-bar" />
            24News
          </div>
          <h1 className="sj-hero__title">{t("explore_topics")}</h1>
          <p className="sj-hero__sub">{t("subjects_sub")}</p>

          {/* ⑪ Aggregate stats */}
          <div className="sj-hero__stats">
            <div className="sj-hero__stat">
              <span className="sj-hero__stat-num">{formatCount(displayCount)}</span>
              <span className="sj-hero__stat-lbl">{t("topics_total")}</span>
            </div>
            {totalLikes > 0 && (
              <>
                <div className="sj-hero__stat-div" aria-hidden />
                <div className="sj-hero__stat">
                  <span className="sj-hero__stat-num">{formatCount(totalLikes)}</span>
                  <span className="sj-hero__stat-lbl">{t("total_likes")}</span>
                </div>
              </>
            )}
            {totalSaves > 0 && (
              <>
                <div className="sj-hero__stat-div" aria-hidden />
                <div className="sj-hero__stat">
                  <span className="sj-hero__stat-num">{formatCount(totalSaves)}</span>
                  <span className="sj-hero__stat-lbl">{t("total_saves")}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ SEARCH BAR ════════════════════════════ */}
      <div className="sj-search-wrap">
        <div className="sj-search-card">
          <div className="sj-search-field-wrap">
            <input
              ref={searchRef}
              className="sj-search-input"
              placeholder={t("search_placeholder")}
              aria-label={t("search_placeholder")}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => { if (suggestions.length) setShowSuggest(true); }}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              autoComplete="off"
            />
            <div className="sj-search-icon"><Icons.Search /></div>
            <div className="sj-search-hint">
              {t("search_hint")} <span className="sj-kbd">/</span>
            </div>

            {showSuggest && (
              <div className="sj-suggestions" ref={suggestRef} role="listbox">
                {suggestLoad && (
                  <div style={{ padding: "12px 16px", fontSize: ".8rem", color: "var(--sj-ink-3)" }}>{t("loading")}</div>
                )}
                {!suggestLoad && suggestions.length === 0 && (
                  <div style={{ padding: "12px 16px", fontSize: ".8rem", color: "var(--sj-ink-3)" }}>{t("no_suggestions")}</div>
                )}
                {!suggestLoad && suggestions.map((s, i) => (
                  <button
                    key={s.id}
                    className={`sj-suggestion-item${i === highlighted ? " active" : ""}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => onSelectSuggestion(s)}
                    role="option"
                    aria-selected={i === highlighted}
                  >
                    <div className="sj-suggestion-name">{highlightMatch(s.name, query)}</div>
                    {s.description && <div className="sj-suggestion-desc">{short(s.description, 70)}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ⑥ Sort + ④ View toggle + Reset */}
          <div className="sj-controls">
            <select
              className="sj-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              aria-label={t("sort_label")}
            >
              <option value="recommended">{t("sort_recommended")}</option>
              <option value="most_liked">{t("sort_most_liked")}</option>
              <option value="most_saved">{t("sort_most_saved")}</option>
              <option value="newest">{t("sort_newest")}</option>
              <option value="most_commented">{t("sort_most_commented")}</option>
            </select>

            <div className="sj-view-toggle" role="group" aria-label={t("view_mode")}>
              <button
                className={`sj-view-btn${viewMode === "grid" ? " active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                title={t("grid_view")}
              >
                <Icons.Grid />
              </button>
              <button
                className={`sj-view-btn${viewMode === "list" ? " active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                title={t("list_view")}
              >
                <Icons.List />
              </button>
            </div>

            <button className="sj-reset-btn" onClick={resetAll} title={t("reset")}>
              <Icons.X /> {t("reset")}
            </button>
          </div>
        </div>
      </div>

      {/* ⑤ Active filter chips */}
      {activeChips.length > 0 && (
        <div className="sj-chips">
          {activeChips.map(chip => (
            <div key={chip.key} className="sj-chip">
              {chip.label}
              <button
                className="sj-chip__remove"
                onClick={chip.onRemove}
                aria-label={`Remove filter ${chip.label}`}
              >
                <Icons.X />
              </button>
            </div>
          ))}
          {activeChips.length > 1 && (
            <button className="sj-chips__clear" onClick={resetAll}>{t("clear_all")}</button>
          )}
        </div>
      )}

      {/* ══════════════════════════ LAYOUT ════════════════════════════════ */}
      <div className="sj-layout">

        {/* SIDEBAR */}
        <aside className="sj-sidebar">
          <div className="sj-sidebar-card">
            <div className="sj-sidebar-head">
              <div className="sj-sidebar-head-left">
                <span className="sj-sidebar-bar" />
                <h2 className="sj-sidebar-title">{t("categories")}</h2>
              </div>
              <button
                className="sj-mobile-cat-toggle"
                onClick={() => setCatOpen(s => !s)}
                aria-expanded={catOpen}
                aria-label={t("toggle_categories")}
              >
                {catOpen ? <Icons.ChevUp /> : <Icons.ChevDown />}
              </button>
            </div>

            <div className={`sj-cat-list-wrap${catOpen ? " open" : ""}`}>
              {loadingCats ? (
                <div style={{ padding: "16px 18px", fontSize: ".8rem", color: "var(--sj-ink-3)" }}>{t("loading")}</div>
              ) : (
                <nav className="sj-cat-list" aria-label={t("categories")}>
 {categoriesMenu.map(c => {
  const id = c.slug ?? String(c.id) ?? "all";
  const active = String(selectedCat) === String(id);

  console.log(c.translations, nLang);

  return (
    <button
      key={id}
      className={`sj-cat-btn${active ? " active" : ""}`}
      onClick={() => {
        setSelectedCat(active ? "all" : id);
        setCatOpen(false);
      }}
      aria-pressed={active}
    >
      {getTranslatedName(c, nLang) || "Category"}
      {active && <span className="sj-cat-dot" aria-hidden />}
    </button>
  );
})}
    
  
</nav>
              )}
            </div>

            <div className="sj-cat-footer">
              {displayCount > 0
                ? <><strong>{displayCount}</strong> {selectedCat !== "all" ? t("topics_in_cat") : t("topics_total")}</>
                : t("no_topics_found")
              }
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="sj-main">
          <div className="sj-results-head">
            <div className="sj-results-count">
              {loading ? t("loading") : (
                <>
                  <strong>{displayCount}</strong>{" "}
                  {displayCount !== 1 ? t("topics_plural") : t("topic_singular")}
                  {searchTerm && <> {t("results_for")} "<strong>{searchTerm}</strong>"</>}
                  {selectedCat !== "all" && (
                    <> {t("results_in")} <strong>{
  (() => {
    const found = categoriesMenu.find(c => String(c.slug) === String(selectedCat));
    return found ? getTranslatedName(found, nLang) : selectedCat;
  })()
}</strong></>
                  )}
                </>
              )}
            </div>
          </div>

          {err && <div className="sj-error">{err}</div>}

          {loading ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <div className="sj-grid">
              <div className="sj-empty">
                <div className="sj-empty__icon">📭</div>
                <p className="sj-empty__title">{t("no_topics_found")}</p>
                <p className="sj-empty__sub">
                  {searchTerm
                    ? t("no_results_for", { term: searchTerm })
                    : t("check_back_soon")
                  }
                </p>
              </div>
            </div>
          ) : (
            <>
              {viewMode === "grid" ? (
                <div className="sj-grid" role="list">
                  {items.map((subject, i) => (
                    <div key={subject.id} role="listitem">
                      <GridCard
                        subject={subject}
                        index={i}
                        onLike={toggleLike}
                        onSave={toggleSave}
                        onShare={shareSubject}
                        sharedId={sharedId}
                        t={t}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sj-list" role="list">
                  {items.map((subject, i) => (
                    <div key={subject.id} role="listitem">
                      <ListCard
                        subject={subject}
                        index={i}
                        onLike={toggleLike}
                        onSave={toggleSave}
                        onShare={shareSubject}
                        sharedId={sharedId}
                        t={t}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* ⑩ Infinite scroll sentinel */}
              <div ref={sentinelRef} className="sj-sentinel" aria-hidden />

              <div className="sj-load-more">
                {nextUrl ? (
                  <button className="sj-load-btn" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? t("loading") : <><Icons.Down /> {t("load_more")}</>}
                  </button>
                ) : (
                  <div className="sj-end-marker">{t("end_of_results")}</div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* ⑧ Back to top — FIX: restored missing JSX, fixes showBtt unused warning */}
      {showBtt && (
        <button
          className="sj-btt"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label={t("back_to_top")}
        >
          <Icons.ArrowUp />
        </button>
      )}

      {/* Auth modal */}
      {showAuthModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 2000,
            background: "rgba(0,0,0,.6)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "sj-in .2s var(--sj-ease) both",
          }}
          onClick={() => setShowAuthModal(false)}
        >
          <div
            style={{
              background: "#1a1a18",
              borderRadius: "18px",
              padding: "32px 32px 28px",
              maxWidth: 400,
              width: "calc(100% - 32px)",
              boxShadow: "0 24px 64px rgba(0,0,0,.7)",
              position: "relative",
              animation: "sj-up .25s var(--sj-ease) both",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowAuthModal(false)}
              style={{
                position: "absolute", top: 14, right: 14,
                width: 30, height: 30,
                borderRadius: "8px",
                border: "1px solid rgba(255,255,255,.1)",
                background: "rgba(255,255,255,.06)",
                color: "rgba(255,255,255,.55)",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", lineHeight: 1,
                transition: "background .15s",
                fontFamily: "var(--sj-sans)",
              }}
              aria-label="Close"
            >
              ×
            </button>

            {/* Lock icon box */}
            <div style={{
              width: 52, height: 52,
              borderRadius: "14px",
              background: "rgba(230,51,17,.15)",
              border: "1.5px solid rgba(230,51,17,.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 20,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="#e63311" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>

            <p style={{
              fontFamily: "var(--sj-sans)",
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#fff",
              margin: "0 0 10px",
              letterSpacing: "-.01em",
            }}>
              {t("auth_required_title") || "Sign in required"}
            </p>

            <p style={{
              fontSize: ".875rem",
              color: "rgba(255,255,255,.5)",
              margin: "0 0 28px",
              lineHeight: 1.65,
            }}>
              {t("auth_required_body") || "You need to be signed in to perform this action. Create a free account or log in to continue."}
            </p>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                onClick={() => setShowAuthModal(false)}
                style={{
                  flex: 1, padding: "11px 20px", borderRadius: "100px",
                  border: "1.5px solid rgba(255,255,255,.15)", background: "transparent",
                  color: "rgba(255,255,255,.75)", fontFamily: "var(--sj-sans)",
                  fontSize: ".85rem", fontWeight: 600, cursor: "pointer",
                  transition: "border-color .15s, background .15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                {t("cancel") || "Cancel"}
              </button>

              <button
                onClick={() => { setShowAuthModal(false); window.location.href = "/register"; }}
                style={{
                  flex: 1, padding: "11px 20px", borderRadius: "100px",
                  border: "1.5px solid var(--sj-red)", background: "transparent",
                  color: "var(--sj-red)", fontFamily: "var(--sj-sans)",
                  fontSize: ".85rem", fontWeight: 600, cursor: "pointer",
                  transition: "background .15s", whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(230,51,17,.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                {t("create_account") || "Create account"}
              </button>

              <button
                onClick={() => { setShowAuthModal(false); window.location.href = "/login"; }}
                style={{
                  flex: 1, padding: "11px 20px", borderRadius: "100px",
                  border: "none", background: "var(--sj-red)", color: "#fff",
                  fontFamily: "var(--sj-sans)", fontSize: ".85rem", fontWeight: 700,
                  cursor: "pointer", transition: "background .15s, transform .15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--sj-red-dk)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--sj-red)"; e.currentTarget.style.transform = "none"; }}
              >
                {t("login") || "Login"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
